<!-- Component for multiple choice elements -->
<template lang="html">
  <div v-if="element">
    <!-- Multiple Choice - Dropdown type -->
    <select
      v-if="element.subType === 'select'"
      v-model="answers[element.nodeName].value"
      :name="element.nodeName"
      class="form-control"
    >
      <option
        v-for="(option, optionIndex) in element.data.choices.data"
        :key="`page-${currentPageNumber}-element-${elementIndex}-option-${optionIndex}`"
        :value="option.text"
        @change="updateDependencyTree(option, element, currentPageNumber, elementIndex)"
      >
        {{ option.text }}
      </option>
    </select>
    <div
      v-else
      class="choice-list"
      :class="alignmentClass"
    >
      <div
        v-for="(choice, choiceIndex) in element.data.choices.data"
        :key="`survey-page-${currentPageNumber}-element-${elementIndex}-choice-${choiceIndex}`"
        class="form-control no-border choice-item"
      >
        <div>
          <!-- Radio / Multiple choice -->
          <label
            v-if="element.subType === 'radio' || element.subType === 'checkbox'"
            class="d-flex align-items-center"
          >
            <!-- Multiple Choice - Checkbox type -->
            <!-- Multiple choice checkboxes values are concat-ed with ; and cannot be mapped directly to v-model -->
            <input
              v-if="element.subType === 'checkbox'"
              @change="handleValue(choice, element, currentPageNumber, elementIndex, $event)"
              :name="element.nodeName"
              :type="element.subType"
              :value="choice.text"
              :checked="checkboxValues.find(e => e === choice.text)"
            />
            <!-- Multiple Choice - Radio button type -->
            <input
              v-else-if="element.subType === 'radio'"
              v-model="answers[element.nodeName].value"
              :name="element.nodeName"
              :type="element.subType"
              :value="choice.text"
              @change="updateDependencyTree(choice, element, currentPageNumber, elementIndex)"
            />
            <span
              v-html="DOMPurify.sanitize(choice.text)"
              class="option-text ms-2">
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'

const VALID_ALIGNMENTS = ['vertical', 'vertical-compact', 'horizontal', 'horizontal-compact']

export default defineComponent({
  name: 'MultipleChoiceElement',
  data () {
    return {
      checkboxValues: []
    }
  },
  inject: [
    'DOMPurify'
  ],
  props: {
    currentPageNumber: Number,
    elementIndex: Number,
    element: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters([
      'answers'
    ]),
    // Set in the survey editor, persisted as data.choices.options.alignment
    alignment () {
      const alignment = this.element.data?.choices?.options?.alignment
      return VALID_ALIGNMENTS.includes(alignment) ? alignment : 'vertical'
    },
    alignmentClass () {
      return `choice-list-${this.alignment}`
    }
  },
  mounted () {
    const value = this.answers[this.element.nodeName].value || ''
    this.checkboxValues = value.split(';')
  },
  methods: {
    ...mapActions([
      'storeDependencyTree'
    ]),
    updateDependencyTree (option, element, pageIndex, elementIndex) {
      this.storeDependencyTree({
        id: option.id,
        value: option.text,
        type: element.type,
        subType: element.subType,
        nodeName: option.nodeName,
        pageIndex: pageIndex,
        elementIndex: elementIndex
      })
    },
    handleValue (choice, element, pageIndex, elementIndex, event) {
      // Array is only used to simplify value comparative searches
      const value = event.target.value
      const valueIndex = this.checkboxValues.findIndex(e => e === value)
      if (event.target.checked) {
        // Check if value exists in array, to avoid duplicate values
        if (valueIndex < 0) {
          this.checkboxValues.push(value)
        }
      } else {
        // Value was unchecked, remove from checkboxvalues
        this.checkboxValues.splice(valueIndex, 1)
      }
      this.updateDependencyTree(choice, element, pageIndex, elementIndex)
      // Convert any changes to string and save to Vuex
      // Values must be concatinated to a string separated with ;. Hello 2006.
      this.answers[this.element.nodeName].value = this.checkboxValues.join(';')
    }
  }
})
</script>

<style lang="less" scoped>
  /* Bootstrap's .form-control forces width: 100% on every choice,
     which keeps them stacked regardless of the chosen alignment. */
  .choice-list-horizontal,
  .choice-list-horizontal-compact {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .choice-list-horizontal .choice-item,
  .choice-list-horizontal-compact .choice-item {
    width: auto;
    max-width: 100%;
    flex: 0 0 auto;
  }

  .choice-list-horizontal {
    column-gap: 1rem;
  }

  .choice-list-horizontal-compact,
  .choice-list-vertical-compact {
    column-gap: .25rem;
  }

  .choice-list-horizontal-compact .choice-item,
  .choice-list-vertical-compact .choice-item {
    padding-top: .1rem;
    padding-bottom: .1rem;
  }
</style>
